import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Banknote, Clock, Loader2, RefreshCw, Scale, UserPlus } from 'lucide-react';

import { DeliverySections } from '../../components/delivery/LogisticsSections';
import { useBusinessAuth } from '../../context/BusinessAuthContext';
import ShopSelect from '../../../components/ShopSelect';
import { fmtPeso } from '../../../utils/formatters';
import { businessSettlementService } from '../../services/businessSettlementService';
import { Button, PageHeader } from "../../../components/ui";

// WP8 settlement desk, Business side (target-v2, dark).
//
// Three jobs that look similar and are not, so they are three views rather than
// one table with a status column:
//
//   Counter       - a Rider is standing there with a trip's cash. Count it and
//                   record what you counted.
//   Discrepancies - a count did not balance. Someone who did not count it closes
//                   the investigation.
//   Obligations   - a customer was never handed their money back. Once the
//                   Rider's cash reaches the store it is refunded to the
//                   shopper's Apparify Wallet.
//
// Nothing here reconciles anything, and each view says so where it could be read
// otherwise. Confirming a receipt closes physical cash custody, not a ledger.
// Resolving a discrepancy closes an investigation; the cash is still owed.
// Refunding an obligation credits the shopper's wallet; it reconciles nothing.

const VIEWS = Object.freeze([
  ['counter', 'At the counter'],
  ['discrepancies', 'Discrepancies'],
  ['obligations', 'Cash obligations'],
]);

// What happens to the difference (owner decision 2026-09-25). A case cannot be
// closed without one of these, so the money always ends somewhere Finance can
// reconcile. First entry is the default.
const DISPOSITIONS = Object.freeze({
  SHORT: [
    ['RIDER_ACCOUNTABLE', 'The rider owes the difference'],
    ['WRITTEN_OFF', 'Write off the difference as a business loss'],
    ['COUNT_CORRECTED', 'A recount found all of it'],
  ],
  SURPLUS: [
    ['RETURNED_TO_RIDER', 'The extra was handed back to the rider'],
    ['KEPT_UNIDENTIFIED', 'Keep the extra as unidentified cash (not revenue)'],
    ['COUNT_CORRECTED', 'A recount found it matches'],
  ],
});

const DISPOSITION_DONE = Object.freeze({
  RIDER_ACCOUNTABLE: 'The difference is now a receivable owed by the rider, under Finance > Receivables.',
  WRITTEN_OFF: 'The difference is recorded as a business loss.',
  COUNT_CORRECTED: 'The recount is recorded; the original count stays on file.',
  RETURNED_TO_RIDER: 'The extra is recorded as handed back to the rider.',
  KEPT_UNIDENTIFIED: 'The extra is recorded as unidentified cash, not revenue.',
});

const ORIGIN_TEXT = Object.freeze({
  CUSTOMER_REFUSED: 'the customer did not confirm the handover',
  WINDOW_EXPIRED: 'the confirmation window closed',
});

/**
 * Parse a peso amount typed by a person into centavos.
 *
 * String parsing rather than `Number(value) * 100`, because 349.99 * 100 is not
 * 34999 in binary floating point and money must not be rounded into correctness.
 *
 * Zero is deliberately allowed, unlike the legacy COD parser: a Rider who hands
 * over nothing is a full shortage, which is a real count the receiver must be
 * able to record, not an invalid entry.
 */
export function countedToCents(value) {
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const cents = (Number(match[1]) * 100) + Number((match[2] || '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
}

export function describeAge(hours) {
  if (hours == null) return 'just now';
  if (hours < 1) return 'under an hour old';
  if (hours < 24) return `${Math.floor(hours)}h old`;
  return `${Math.floor(hours / 24)}d old`;
}

function Empty({ icon: Icon, title, body }) {
  return (
    <section className="rounded-2xl border border-dashed p-8 text-center">
      <Icon className="mx-auto h-7 w-7 text-neutral-400" aria-hidden="true" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mx-auto mt-1 max-w-prose text-sm text-neutral-500">{body}</p>
    </section>
  );
}

function Overdue() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-danger-subtle px-3 py-1 text-xs font-semibold text-danger">
      <Clock className="h-3.5 w-3.5" aria-hidden="true" /> Past due
    </span>
  );
}

export default function DeliverySettlementDesk() {
  const auth = useBusinessAuth();
  const businessId = auth?.businessId;
  const [view, setView] = useState('counter');
  const [remittances, setRemittances] = useState([]);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [ownerCandidates, setOwnerCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // One map per kind of input, keyed by record id, so typing in one card never
  // re-renders the value of another.
  const [counted, setCounted] = useState({});
  const [reasons, setReasons] = useState({});
  const [categories, setCategories] = useState({});
  // H3 owner override: the Owner may close a case alone only when nobody else
  // qualified exists (the server proves it and records the override forever).
  // Without this a store run by its owner alone could never close a case, and a
  // short count held its rider from new trips for good.
  const [overrides, setOverrides] = useState({});
  const [recounts, setRecounts] = useState({});
  // The obligation whose wallet refund is waiting for a second, confirming tap.
  const [confirmingRefund, setConfirmingRefund] = useState(null);
  const isOwner = String(auth?.role || '').toUpperCase() === 'OWNER';
  const [owners, setOwners] = useState({});

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError('');
    const [sessions, cases, debts] = await Promise.all([
      businessSettlementService.remittances(businessId, 'SUBMITTED').catch(() => null),
      businessSettlementService.discrepancies(businessId, { status: 'NEEDS_DISPOSITION' }).catch(() => null),
      businessSettlementService.cashObligations(businessId, { status: 'OPEN' }).catch(() => null),
    ]);
    setRemittances(sessions?.items || []);
    setDiscrepancies(cases?.items || []);
    setObligations(debts?.items || []);
    setOwnerCandidates(debts?.ownerCandidates || []);
    setLoading(false);
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (token, action, message) => {
    setBusy(token);
    setError('');
    setNotice('');
    try {
      const result = await action();
      setNotice(typeof message === 'function' ? message(result) : message);
      await load();
    } catch (actionError) {
      setError(actionError?.response?.data?.message || actionError?.message
        || 'That did not record. Refresh and try again.');
    } finally {
      setBusy('');
    }
  }, [load]);

  // Derived during render. An effect mirroring this into state would be a second
  // source of truth that can disagree with the list it came from.
  const overdueCount = useMemo(
    () => obligations.filter((item) => item.overdue).length,
    [obligations],
  );
  const counts = useMemo(() => ({
    counter: remittances.length,
    discrepancies: discrepancies.length,
    obligations: obligations.length,
  }), [remittances.length, discrepancies.length, obligations.length]);

  return (
    <div className="w-full space-y-5">
      <PageHeader
        title="Settlement desk"
        description={<>
          Where a rider hands in a trip&apos;s cash after delivering. Take the cash, look into a
          count that did not balance, and track money a customer never got back.
          <span className="mt-1 block">Counting cash here does not reconcile it. Finance does that under COD custody.</span>
        </>}
        actions={<Button size="sm" icon={RefreshCw} onClick={load} disabled={loading || Boolean(busy)}>Refresh</Button>}
        navigation={<DeliverySections current="settlement" />}
      />

      <nav className="mt-5 flex flex-wrap gap-2" aria-label="Settlement views">
        {VIEWS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setView(value)}
            aria-pressed={view === value}
            className={`min-h-11 rounded-xl border px-4 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
              view === value ? 'border-accent bg-accent-subtle text-accent' : ''
            }`}
          >
            {label}
            {counts[value] > 0 ? (
              <span className="ml-2 tabular-nums text-neutral-500">{counts[value]}</span>
            ) : null}
            {value === 'obligations' && overdueCount > 0 ? (
              <span className="ml-2 rounded-full bg-danger px-2 py-0.5 text-xs text-on-accent">
                {overdueCount} late
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      {notice ? (
        <p className="mt-4 rounded-xl bg-success-subtle p-3 text-sm text-success">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-danger-subtle p-3 text-sm text-danger">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {error}
        </p>
      ) : null}

      {loading ? (
        <div className="mt-6 grid min-h-48 place-items-center">
          <Loader2 className="h-7 w-7 animate-spin" aria-label="Loading settlement work" />
        </div>
      ) : null}

      {!loading && view === 'counter' ? (
        remittances.length === 0 ? (
          <div className="mt-6">
            <Empty
              icon={Banknote}
              title="Nobody is waiting"
              body="When a Rider presents a trip's cash it appears here to be counted."
            />
          </div>
        ) : (
          <div className="mt-6 grid gap-3">
            {remittances.map((session) => {
              const cents = countedToCents(counted[session.remittanceSessionId]);
              const token = `confirm-${session.remittanceSessionId}`;
              return (
                <article key={session.remittanceSessionId} className="rounded-2xl border p-4 dark:border-white/10">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <strong>Trip #{session.deliveryRunId}</strong>
                      <p className="mt-1 text-sm text-neutral-500">
                        {session.riderName || `Rider #${session.riderBusinessMemberId}`} ·{' '}
                        {session.contributorCount} collection(s)
                        {session.sessionKind === 'EMERGENCY_RECOVERY' ? ' · handed over early' : ''}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold tabular-nums dark:bg-white/10">
                      Rider says {fmtPeso(session.expectedAmountCents)}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-semibold" htmlFor={`counted-${session.remittanceSessionId}`}>
                        What you counted (PHP)
                      </label>
                      <input
                        id={`counted-${session.remittanceSessionId}`}
                        inputMode="decimal"
                        placeholder="0.00"
                        value={counted[session.remittanceSessionId] ?? ''}
                        onChange={(event) => setCounted((current) => ({
                          ...current, [session.remittanceSessionId]: event.target.value,
                        }))}
                        className="mt-2 min-h-11 w-full rounded-xl border bg-transparent px-3 tabular-nums"
                      />
                    </div>
                    <p className="max-w-prose self-end text-sm text-neutral-500">
                      Enter what is physically in front of you, not what it should be. A
                      difference opens a case; it is not corrected here.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={Boolean(busy) || cents === null}
                    onClick={() => run(
                      token,
                      () => businessSettlementService.confirmRemittance(businessId, session.remittanceSessionId, {
                        countedAmountCents: cents,
                        expectedVersion: session.version,
                      }),
                      // Say what the count found. A short count used to read the same
                      // as a matched one, leaving the owner to discover the case.
                      (result) => (result?.finding === 'SHORT'
                        ? `Recorded. The count is ${fmtPeso(result.discrepancyAmountCents)} short, so a discrepancy case is open under Discrepancies and the rider cannot take new trips until someone else resolves it. The rider can finish this trip.`
                        : result?.finding === 'SURPLUS'
                          ? `Recorded. The count is ${fmtPeso(result.discrepancyAmountCents)} over, so a discrepancy case is open under Discrepancies. The rider can finish this trip.`
                          : 'Recorded. The count matches, and this trip’s cash is settled.'),
                    )}
                    className="business-primary-action mt-3 min-h-11 rounded-xl bg-accent px-4 font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 disabled:opacity-50"
                  >
                    {busy === token ? 'Recording…' : 'Record what I counted'}
                  </button>
                </article>
              );
            })}
          </div>
        )
      ) : null}

      {!loading && view === 'discrepancies' ? (
        discrepancies.length === 0 ? (
          <div className="mt-6">
            <Empty icon={Scale} title="No cases need a decision" body="Every counted handover balanced, or its difference has an outcome." />
          </div>
        ) : (
          <div className="mt-6 grid gap-3">
            {discrepancies.map((item) => {
              const reason = String(reasons[item.discrepancyCaseId] || '').trim();
              const token = `resolve-${item.discrepancyCaseId}`;
              const choices = DISPOSITIONS[item.direction] || DISPOSITIONS.SHORT;
              const category = categories[item.discrepancyCaseId] || choices[0][0];
              const recountCents = countedToCents(recounts[item.discrepancyCaseId]);
              const needsRecount = category === 'COUNT_CORRECTED';
              const override = overrides[item.discrepancyCaseId] || { on: false, reason: '' };
              const overrideReason = String(override.reason || '').trim();
              const setOverride = (patch) => setOverrides((current) => ({
                ...current, [item.discrepancyCaseId]: { ...override, ...patch },
              }));
              return (
                <article key={item.discrepancyCaseId} className="rounded-2xl border p-4 dark:border-white/10">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <strong className="tabular-nums">
                        {item.direction === 'SHORT' ? 'Short' : 'Surplus'} {fmtPeso(item.amountCents)}
                      </strong>
                      <p className="mt-1 text-sm text-neutral-500">
                        Trip #{item.deliveryRunId} · {item.riderName || `Rider #${item.riderBusinessMemberId}`}
                      </p>
                    </div>
                    {item.overdue ? <Overdue /> : null}
                  </div>
                  <dl className="mt-3 grid max-w-md grid-cols-3 gap-2 text-sm">
                    <div><dt className="text-neutral-500">Expected</dt><dd className="font-semibold tabular-nums">{fmtPeso(item.expectedAmountCents)}</dd></div>
                    <div><dt className="text-neutral-500">Counted</dt><dd className="font-semibold tabular-nums">{fmtPeso(item.staffCountedAmountCents)}</dd></div>
                    <div><dt className="text-neutral-500">{item.direction === 'SHORT' ? 'Missing' : 'Extra'}</dt><dd className="font-semibold tabular-nums">{fmtPeso(item.amountCents)}</dd></div>
                  </dl>
                  {item.status === 'RESOLVED' ? (
                    <p className="mt-2 max-w-prose rounded-xl bg-warning-subtle p-3 text-sm text-warning">
                      This was closed earlier without deciding what happens to the money. Record the outcome so Finance can reconcile the trip.
                    </p>
                  ) : null}
                  <p className="mt-2 max-w-prose text-sm text-neutral-500">
                    Whoever counted this cannot be the one to record the outcome.
                  </p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <div className="text-sm font-semibold">
                        <span>What happens to the difference?</span>
                      <ShopSelect
                        value={category}
                        onValueChange={(value) => setCategories((current) => ({
                          ...current, [item.discrepancyCaseId]: value,
                        }))}
                        options={choices.map(([value, label]) => ({ value, label }))}
                        ariaLabel="What happens to the difference?"
                        align="start"
                        minPanelWidth={240}
                        triggerClassName="mt-2 min-h-11 w-full rounded-control px-3 text-sm font-normal"
                        panelClassName="py-1"
                      />
                      </div>
                      {needsRecount ? (
                        <div className="mt-2">
                          <label className="text-sm font-semibold" htmlFor={`recount-${item.discrepancyCaseId}`}>
                            Recounted amount (PHP)
                          </label>
                          <input
                            id={`recount-${item.discrepancyCaseId}`}
                            inputMode="decimal"
                            value={recounts[item.discrepancyCaseId] || ''}
                            onChange={(event) => setRecounts((current) => ({
                              ...current, [item.discrepancyCaseId]: event.target.value,
                            }))}
                            className="mt-2 min-h-11 w-full rounded-xl border bg-transparent px-3 tabular-nums"
                          />
                          <p className="mt-1 text-xs text-neutral-500">
                            Only a recount that matches {fmtPeso(item.expectedAmountCents)} closes it this way.
                          </p>
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <label className="text-sm font-semibold" htmlFor={`reason-${item.discrepancyCaseId}`}>
                        Why
                      </label>
                      <input
                        id={`reason-${item.discrepancyCaseId}`}
                        value={reasons[item.discrepancyCaseId] || ''}
                        onChange={(event) => setReasons((current) => ({
                          ...current, [item.discrepancyCaseId]: event.target.value,
                        }))}
                        maxLength={1000}
                        aria-describedby={`reason-hint-${item.discrepancyCaseId}`}
                        className="mt-2 min-h-11 w-full rounded-xl border bg-transparent px-3"
                      />
                      <p id={`reason-hint-${item.discrepancyCaseId}`} className="mt-1 text-xs text-neutral-500">
                        At least 10 characters. This stays on the record.
                      </p>
                    </div>
                  </div>
                  {isOwner ? (
                    <div className="mt-3 rounded-xl border border-dashed p-3 dark:border-white/15">
                      <label className="flex min-h-11 items-start gap-3 text-sm">
                        <input
                          type="checkbox"
                          checked={override.on}
                          onChange={(event) => setOverride({ on: event.target.checked })}
                          className="mt-1 h-5 w-5 shrink-0"
                        />
                        <span>
                          <strong>No one else here can close this.</strong> Close it as the owner.
                          This is recorded as an owner override, and it is refused if someone
                          else in the store could close it.
                        </span>
                      </label>
                      {override.on ? (
                        <div className="mt-2">
                          <label className="text-sm font-semibold" htmlFor={`override-${item.discrepancyCaseId}`}>
                            Why no one else can close it
                          </label>
                          <input
                            id={`override-${item.discrepancyCaseId}`}
                            value={override.reason}
                            onChange={(event) => setOverride({ reason: event.target.value })}
                            maxLength={1000}
                            className="mt-2 min-h-11 w-full rounded-xl border bg-transparent px-3"
                          />
                          <p className="mt-1 text-xs text-neutral-500">At least 10 characters.</p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    disabled={Boolean(busy) || reason.length < 10 || (override.on && overrideReason.length < 10)
                      || (needsRecount && recountCents === null)}
                    onClick={() => run(
                      token,
                      () => businessSettlementService.resolveDiscrepancy(businessId, item.discrepancyCaseId, {
                        resolutionCategory: category,
                        ...(needsRecount ? { correctedCountCents: recountCents } : {}),
                        resolutionReason: reason,
                        expectedVersion: item.version,
                        ...(override.on ? { ownerOverride: { reason: overrideReason } } : {}),
                      }),
                      (result) => `Outcome recorded${override.on ? ' as an owner override' : ''}. ${DISPOSITION_DONE[category] || ''} The trip's cash is now ready for Finance to reconcile.${result?.riderWorkHoldReleased ? ' The rider can take new trips again.' : ''}`,
                    )}
                    className="business-primary-action mt-3 min-h-11 rounded-xl bg-accent px-4 font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 disabled:opacity-50"
                  >
                    {busy === token ? 'Recording…' : 'Record the outcome'}
                  </button>
                </article>
              );
            })}
          </div>
        )
      ) : null}

      {!loading && view === 'obligations' ? (
        obligations.length === 0 ? (
          <div className="mt-6">
            <Empty
              icon={UserPlus}
              title="Nothing outstanding"
              body="No customer is waiting on cash that was never physically returned."
            />
          </div>
        ) : (
          <div className="mt-6 grid gap-3">
            {obligations.map((item) => {
              const refundToken = `refund-${item.obligationId}`;
              const ownerToken = `owner-${item.obligationId}`;
              const confirming = confirmingRefund === item.obligationId;
              return (
                <article key={item.obligationId} className="rounded-2xl border p-4 dark:border-white/10">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <strong className="tabular-nums">{fmtPeso(item.amountCents)} owed to a customer</strong>
                      <p className="mt-1 text-sm text-neutral-500">
                        {item.orderReference || `Order #${item.orderId}`} ·{' '}
                        {ORIGIN_TEXT[item.originReason] || 'the cash was never handed back'} ·{' '}
                        {describeAge(item.ageHours)}
                      </p>
                    </div>
                    {item.overdue ? <Overdue /> : null}
                  </div>

                  {item.cashReceivedByStore ? (
                    <div className="mt-3" data-testid={`refund-${item.obligationId}`}>
                      <p className="max-w-prose text-sm">
                        The rider handed this cash to the store. Refund it to the customer&apos;s
                        Apparify Wallet; they see it in their order and wallet straight away.
                      </p>
                      {confirming ? (
                        <div className="mt-3 max-w-prose rounded-xl border border-info bg-info-subtle p-3">
                          <p className="text-sm font-semibold">
                            Refund {fmtPeso(item.amountCents)} to this customer&apos;s wallet now? This can&apos;t be undone.
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={Boolean(busy)}
                              onClick={() => setConfirmingRefund(null)}
                              className="min-h-11 rounded-xl border px-4 font-semibold disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={Boolean(busy)}
                              onClick={async () => {
                                await run(
                                  refundToken,
                                  () => businessSettlementService.refundObligationToWallet(businessId, item.obligationId, {
                                    expectedVersion: item.version,
                                  }),
                                  `Refunded ${fmtPeso(item.amountCents)} to the customer's Apparify Wallet.`,
                                );
                                setConfirmingRefund(null);
                              }}
                              className="business-primary-action min-h-11 rounded-xl bg-accent px-4 font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 disabled:opacity-50"
                            >
                              {busy === refundToken ? 'Refunding…' : `Yes, refund ${fmtPeso(item.amountCents)}`}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={Boolean(busy)}
                          onClick={() => setConfirmingRefund(item.obligationId)}
                          className="business-primary-action mt-3 min-h-11 rounded-xl bg-accent px-4 font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 disabled:opacity-50"
                        >
                          Refund {fmtPeso(item.amountCents)} to the shopper&apos;s wallet
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="mt-3 max-w-prose rounded-xl bg-warning-subtle p-3 text-sm text-warning">
                      Waiting for the rider to hand this cash to the store. Once the trip&apos;s cash
                      is counted at the counter, you can refund it to the customer&apos;s Apparify Wallet.
                    </p>
                  )}

                  {item.ownerBusinessMemberId ? (
                    <p className="mt-3 text-sm text-neutral-500">
                      Followed up by{' '}
                      {ownerCandidates.find(
                        (candidate) => candidate.businessMemberId === item.ownerBusinessMemberId,
                      )?.name || `member #${item.ownerBusinessMemberId}`}.
                    </p>
                  ) : ownerCandidates.length > 0 ? (
                    <details className="mt-3 text-sm">
                      <summary className="cursor-pointer font-semibold">Ask someone else to follow this up (optional)</summary>
                      <div className="mt-2 block max-w-sm text-sm font-semibold">
                        <span>Who follows this up?</span>
                      <ShopSelect
                        value={owners[item.obligationId] || ''}
                        onValueChange={(value) => setOwners((current) => ({
                          ...current, [item.obligationId]: value,
                        }))}
                        options={[
                          { value: '', label: 'Choose someone…' },
                          ...ownerCandidates.map((candidate) => ({
                            value: candidate.businessMemberId,
                            label: `${candidate.name || `Member #${candidate.businessMemberId}`}${candidate.role ? ` · ${candidate.role}` : ''}`,
                          })),
                        ]}
                        ariaLabel="Who follows this up?"
                        align="start"
                        minPanelWidth={240}
                        triggerClassName="mt-2 min-h-11 w-full rounded-control px-3 text-sm font-normal"
                        panelClassName="py-1"
                      />
                      </div>
                      <button
                        type="button"
                        disabled={Boolean(busy) || !owners[item.obligationId]}
                        onClick={() => run(
                          ownerToken,
                          () => businessSettlementService.assignObligationOwner(businessId, item.obligationId, {
                            ownerBusinessMemberId: Number(owners[item.obligationId]),
                          }),
                          'Recorded who follows this up. The refund is still owed.',
                        )}
                        className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border px-4 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
                      >
                        <UserPlus className="h-4 w-4" aria-hidden="true" />
                        {busy === ownerToken ? 'Recording…' : 'Assign'}
                      </button>
                    </details>
                  ) : null}
                </article>
              );
            })}
          </div>
        )
      ) : null}
    </div>
  );
}
